import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Card, 
  Button, 
  Input, 
  Space, 
  DatePicker, 
  Select, 
  notification,
  Drawer,
  Row,
  Col,
  Typography,
  Switch
} from 'antd';
import { 
  EnvironmentOutlined, 
  CompassOutlined, 
  CloseCircleOutlined,
  InfoCircleOutlined,
  SettingOutlined,
  GiftOutlined,
  EditOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import jalaliday from 'jalaliday';
import 'dayjs/locale/fa';
import moment from 'moment-jalaali';
// Neshan Maps SDK is loaded via script tag in index.html
declare global {
  interface Window {
    L: any;
  }
}
import { httpService } from '../../services/httpService';
import { NESHAN_API_KEY, NESHAN_MAP_KEY, NESHAN_SDK_URL, NESHAN_CSS_URL } from '../../config';
import { useTheme } from '../../theme/ThemeProvider';
import './styles.css';

// Configure dayjs for Jalali calendar
dayjs.extend(jalaliday);
dayjs.calendar('jalali');
dayjs.locale('fa');

// Configure moment-jalaali
moment.loadPersian({ dialect: 'persian-modern' });

interface Location {
  lat: number;
  lng: number;
  address: string;
  title?: string;
  city?: string;
  plaque?: string;
}

interface MapMarker {
  id: string;
  position: [number, number];
  marker: any; // Neshan SDK marker
}

// --- CreateTrip API Model Types ---
export interface TripLocation {
  id?: string;
  createdAt?: string;
  updatedAt?: string;
  isActive?: boolean;
  lat: number;
  long: number;
  title?: string;
  address: string;
  city?: string;
  plaque?: string;
}

export interface TimeObject {
  hour: number;
  minute: number;
}





export interface CreateTripModel {
  type: TripType;
  durationMinutes: number;
  endTime: string; // 'HH:mm'
  endDate: string | null; // 'YYYY-MM-DD' or null when empty
  startTime: string; // 'HH:mm'
  startDate: string; // ISO string
  destination: TripLocation;
  source: TripLocation;
  tripItemRequests: TripItemRequest[];
}

// Trip type enum matching backend
export enum TripType {
  None = 0,
  Transfer = 1,    
  Dedicated = 2,   
  Intercity = 3
}

// TripItemType enum matching backend
export enum TripItemType {
  None = 0,
  Distance = 1,
  Duration = 2,
  RestrictArea = 100,
  Suburb = 101,
  StopMinutes = 102,
  RoundTrip = 103,
}

// Group enum matching backend
export enum Group {
  None = 0,
  Bronze = 1,    // گروه خودروهای برنزی
  Silver = 2,    // گروه خودروهای نقره ای
  Gold = 3,      // گروه خودروهای طلایی
  VIP = 4        // گروه خودروهای VIP
}

// TripState enum matching backend
export enum TripState {
  None = 0,              // نامشخص
  Requested = 1,         // درخواست
  PrimaryReserve = 2,    // رزرو اولیه
  DriverAssigned = 3,    // تخصیص راننده
  FinalResere = 4,       // رزرو نهایی
  Start = 5,             // در حال سفر
  End = 6                // پایان سفر
}

interface TripItemRequest {
  count: number;
  tripItemType: TripItemType;
}

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<any>(null); // Neshan SDK map
  const markers = useRef<MapMarker[]>([]);
  const activeFieldRef = useRef<"source" | "destination" | null>("source");
  const [mapKey] = useState<number>(Date.now());
  
  const [source, setSource] = useState<Location | null>(null);
  const [destination, setDestination] = useState<Location | null>(null);
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs());
  const [selectedTime, setSelectedTime] = useState<Dayjs | null>(null);
  const [travelDuration, setTravelDuration] = useState<number | null>(null);
  const [selectedServiceType, setSelectedServiceType] = useState<TripType>(TripType.Transfer);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedDrawerType, setSelectedDrawerType] = useState<TripType | null>(null);
  const [tripOptionsDrawerVisible, setTripOptionsDrawerVisible] = useState(false);
  const [locationEditDrawerVisible, setLocationEditDrawerVisible] = useState(false);
  const [editingLocationType, setEditingLocationType] = useState<"source" | "destination" | null>(null);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [stopDuration, setStopDuration] = useState<number | null>(null);
  const [isRoundTrip, setIsRoundTrip] = useState(false);
  const [trafficPlan, setTrafficPlan] = useState(false);
  const [suburbanTrip, setSuburbanTrip] = useState(false);

  // Field visibility configuration based on trip type
  const fieldVisibilityConfig = {
    [TripType.None]: {
      dateTimeStart: true,
      source: true,
      destination: true,
      duration: false
    },
    [TripType.Transfer]: {
      dateTimeStart: true,
      source: true,
      destination: true,
      duration: false
    },
    [TripType.Intercity]: {
      dateTimeStart: true,
      source: true,
      destination: true,
      duration: false
    },
    [TripType.Dedicated]: {
      dateTimeStart: true,
      source: true,
      destination: false,
      duration: true
    }
  };

  // Helper function to check if a field should be visible
  const shouldShowField = (fieldName: 'dateTimeStart' | 'source' | 'destination' | 'duration') => {
    return fieldVisibilityConfig[selectedServiceType]?.[fieldName] ?? true;
  };

  const minDuration = 4; // حداقل مدت سفر (ساعت)
  const maxDuration = selectedTime ? 24 - selectedTime.hour() : 0;
  const durationOptions = [];
  for (let i = minDuration; i <= maxDuration; i++) {
    durationOptions.push(
      <Select.Option key={i} value={i}>{i} ساعت</Select.Option>
    );
  }





  // Get address from coordinates
  const getAddressFromCoordinates = useCallback(async (lat: number, lng: number): Promise<string> => {
    try {
      // Create AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 seconds timeout
      
             const response = await fetch(
         `https://api.neshan.org/v4/reverse?lat=${lat}&lng=${lng}`,
         {
           headers: {
             'Api-Key': NESHAN_API_KEY
           },
           signal: controller.signal
         }
       );
      
      // Clear timeout if request completes successfully
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const data = await response.json();
        return data.formatted_address || `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        console.error('❌ API request timed out after 30 seconds');
      } else {
        console.error('❌ Error getting address:', error);
      }
    }
    
    return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
  }, []);

  // Create marker icon
  const createMarkerIcon = useCallback((color: string, label?: string) => {
    const iconHtml = `
      <div style="
        width: 30px;
        height: 40px;
        position: relative;
        z-index: 1000;
      ">
        <div style="
          width: 30px;
          height: 30px;
          background: ${color};
          border: 4px solid white;
          border-radius: 50%;
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 10px;
          font-weight: bold;
          color: white;
          text-shadow: 0 1px 2px rgba(0,0,0,0.5);
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          position: absolute;
          top: 0;
          left: 0;
        ">
          ${label || ''}
        </div>
        <div style="
          width: 2px;
          height: 10px;
          background: ${color};
          position: absolute;
          bottom: 0;
          left: -60%;
          
          border-radius: 1px;
        "></div>
      </div>
    `;

    return window.L.divIcon({
      html: iconHtml,
      className: 'custom-div-icon',
      iconSize: [30, 40],
      iconAnchor: [15, 40],
      popupAnchor: [0, -40]
    });
  }, []);

  // Add marker to map
  const addMarker = useCallback((
    position: [number, number], // [lng, lat] in WGS84
    color: string,
    label?: string,
    id?: string
  ): MapMarker | undefined => {
    const map = mapInstance.current;
    
    if (!map) {
      console.warn('Map not initialized, cannot add marker');
      return;
    }

    try {
      // Create marker icon
      const icon = createMarkerIcon(color, label);
      
      // Create marker at [lat, lng] - Leaflet expects lat first
      const marker = window.L.marker([position[1], position[0]], { 
        icon,
        zIndexOffset: 1000,
        riseOnHover: true,
        riseOffset: 250
      }).addTo(map);



      const mapMarker: MapMarker = {
        id: id ?? `marker-${Date.now()}`,
        position,
        marker,
      };

      markers.current.push(mapMarker);
      return mapMarker;
    } catch (error) {
      console.error('Error adding marker:', error);
      return;
    }
  }, [createMarkerIcon]);

  // Remove marker by id
  const removeMarker = useCallback((id: string) => {
    const markerIndex = markers.current.findIndex(m => m.id === id);
    if (markerIndex !== -1) {
      const mapMarker = markers.current[markerIndex];
      if (mapInstance.current && mapMarker.marker) {
        try {
          mapInstance.current.removeLayer(mapMarker.marker);
        } catch (error) {
          console.warn('Error removing marker layer:', error);
        }
      }
      markers.current.splice(markerIndex, 1);
    }
  }, []);



    // Initialize map with better error handling
  useEffect(() => {
    let isMounted = true;
    
    const initializeMapSafely = async () => {
      if (!mapRef.current || mapInstance.current) return;

      try {
        // Clear any existing markers
        markers.current = [];

        // Check if Neshan SDK is loaded
        if (typeof (window as any).L === 'undefined') {
          // Load SDK if not available
          await new Promise<void>((resolve, reject) => {
            const existingScript = document.querySelector('script[src*="neshan-sdk"]');
            if (existingScript) {
              resolve();
              return;
            }

                         // Create CSS link
             const cssLink = document.createElement('link');
             cssLink.rel = 'stylesheet';
             cssLink.href = NESHAN_CSS_URL;
             document.head.appendChild(cssLink);

             // Create script element
             const script = document.createElement('script');
             script.src = NESHAN_SDK_URL;
            script.onload = () => {
              setTimeout(() => {
                if (typeof (window as any).L !== 'undefined') {
                  resolve();
                } else {
                  reject(new Error('Neshan SDK failed to load'));
                }
              }, 200);
            };
            script.onerror = () => reject(new Error('Failed to load Neshan SDK script'));
            document.head.appendChild(script);
          });
        }

        // Double check if component is still mounted before creating map
        if (!isMounted || !mapRef.current) return;

        // Create a new container div inside the ref to avoid conflicts
        const mapContainer = document.createElement('div');
        mapContainer.style.width = '100%';
        mapContainer.style.height = '100%';
        mapRef.current.innerHTML = '';
        mapRef.current.appendChild(mapContainer);

                 // Create map instance
         const map = new (window as any).L.Map(mapContainer, {
           key: NESHAN_MAP_KEY,
           maptype: "neshan",
           poi: false,
           traffic: false,
           center: [35.6892, 51.3890],
           zoom: 13,
         });

        map.setMapType("neshan");
        
        if (!isMounted) {
          map.remove();
          return;
        }

        mapInstance.current = map;

        // Add click event listener
        map.on('click', (e: any) => {
          if (!isMounted) return;
          
          const { lat, lng } = e.latlng;
          const currentActiveField = activeFieldRef.current;
          
          getAddressFromCoordinates(lat, lng).then(address => {
            if (!isMounted) return;
            
            const location: Location = { lat, lng, address };
            
            if (currentActiveField === 'source') {
              setSource(location);
              removeMarker('source-marker');
              addMarker([lng, lat], '#1890ff', 'مبدا', 'source-marker');
              notification.success({ message: 'مبدا تنظیم شد' });
              activeFieldRef.current = "destination";
            } else if (currentActiveField === 'destination') {
              setDestination(location);
              removeMarker('destination-marker');
              addMarker([lng, lat], '#52c41a', 'مقصد', 'destination-marker');
              notification.success({ message: 'مقصد تنظیم شد' });
              activeFieldRef.current = null;
            }
                     }).catch(() => {
             if (!isMounted) return;
             
             const location: Location = { lat, lng, address: `${lat.toFixed(6)}, ${lng.toFixed(6)}` };
            
            if (currentActiveField === 'source') {
              setSource(location);
              removeMarker('source-marker');
              addMarker([lng, lat], '#1890ff', 'مبدا', 'source-marker');
              notification.success({ message: 'مبدا تنظیم شد (آدرس در دسترس نبود)' });
              activeFieldRef.current = "destination";
            } else if (currentActiveField === 'destination') {
              setDestination(location);
              removeMarker('destination-marker');
              addMarker([lng, lat], '#52c41a', 'مقصد', 'destination-marker');
              notification.success({ message: 'مقصد تنظیم شد (آدرس در دسترس نبود)' });
              activeFieldRef.current = null;
            }
          });
        });

      } catch (error) {
        if (!isMounted) return;
        console.error('Error initializing map:', error);
      }
    };

    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      initializeMapSafely();
    }, 100);

    return () => {
      isMounted = false;
      clearTimeout(timer);
      
      if (mapInstance.current) {
        try {
          mapInstance.current.remove();
        } catch (error) {
          console.warn('Error removing map instance:', error);
        }
        mapInstance.current = null;
      }
      
      markers.current = [];
    };
  }, [mapKey]); // Only depend on mapKey

  // Separate effect to handle marker updates when source/destination change
  useEffect(() => {
    if (!mapInstance.current) return;

    // Remove existing markers
    removeMarker('source-marker');
    removeMarker('destination-marker');

    // Add new markers if locations exist
    if (source) {
      addMarker([source.lng, source.lat], '#1890ff', 'مبدا', 'source-marker');
    }
    if (destination) {
      addMarker([destination.lng, destination.lat], '#52c41a', 'مقصد', 'destination-marker');
    }
  }, [source, destination, addMarker, removeMarker]);

  useEffect(() => {
    setTravelDuration(null);
  }, [selectedTime]);

  // Use current location for source/destination
  const useCurrentLocation = async (type: "source" | "destination") => {
    if (!navigator.geolocation) {
      notification.warning({ message: 'مرورگر شما از موقعیت مکانی پشتیبانی نمی‌کند' });
      return;
    }

    try {
      notification.info({ message: 'در حال دریافت موقعیت فعلی...' });
      
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000
        });
      });
      
      const { latitude, longitude } = position.coords;
      const address = await getAddressFromCoordinates(latitude, longitude);
      const location: Location = { lat: latitude, lng: longitude, address };
      
      if (type === "source") {
        setSource(location);
        removeMarker('source-marker');
        addMarker([location.lng, location.lat], '#1890ff', 'مبدا', 'source-marker');
        notification.success({ message: 'موقعیت فعلی به عنوان مبدا تنظیم شد' });
      } else {
        setDestination(location);
        removeMarker('destination-marker');
        addMarker([location.lng, location.lat], '#52c41a', 'مقصد', 'destination-marker');
        notification.success({ message: 'موقعیت فعلی به عنوان مقصد تنظیم شد' });
      }
    } catch (error) {
      notification.error({ message: 'خطا در دریافت موقعیت فعلی. لطفا دوباره تلاش کنید.' });
    }
  };

  // Handle field click
  const handleFieldClick = (field: "source" | "destination") => {
    activeFieldRef.current = field;
    notification.info({ message: field === "source" ? "فیلد مبدا انتخاب شد. روی نقشه کلیک کنید" : "فیلد مقصد انتخاب شد. روی نقشه کلیک کنید" });
  };

  // Set source as default active field when component mounts
  useEffect(() => {
    if (mapInstance.current && !activeFieldRef.current) {
      activeFieldRef.current = "source";
      notification.info({ message: "فیلد مبدا انتخاب شد. روی نقشه کلیک کنید" });
    }
  }, [mapInstance.current]);

  // Clear location
  const clearLocation = (type: "source" | "destination") => {
    if (type === "source") {
      setSource(null);
      removeMarker('source-marker');
    } else {
      setDestination(null);
      removeMarker('destination-marker');
    }
  };


  // Helper to build CreateTripModel from current state
  const buildCreateTripModel = useCallback((): CreateTripModel => {
    const durationMinutes = travelDuration ? travelDuration * 60 : 0;

    const startDateISO = selectedDate ? selectedDate.toISOString() : '';
    const startTimeStr = selectedTime
      ? selectedTime.format('HH:mm')
      : '00:00';

    const endDateStr = selectedDate && travelDuration
      ? selectedDate.format('YYYY-MM-DD')
      : null;

    const endTimeStr = selectedTime && travelDuration
      ? selectedTime.add(travelDuration, 'hour').format('HH:mm')
      : '00:00';

    // Build tripItemRequests based on user selections
    const tripItemRequests: TripItemRequest[] = [];

    // Add Duration if travelDuration is set (for dedicated trips)
    if (travelDuration && selectedServiceType === TripType.Dedicated) {
      console.log('🔍 Adding Duration item:', { travelDuration, selectedServiceType });
      tripItemRequests.push({
        count: travelDuration,
        tripItemType: TripItemType.Duration
      });
    }

    // Add RestrictArea if trafficPlan is enabled
    if (trafficPlan) {
      tripItemRequests.push({
        count: 1,
        tripItemType: TripItemType.RestrictArea
      });
    }

    // Add Suburb if suburbanTrip is enabled
    if (suburbanTrip) {
      tripItemRequests.push({
        count: 1,
        tripItemType: TripItemType.Suburb
      });
    }

    // Add StopMinutes if stopDuration is set
    if (stopDuration && stopDuration > 0) {
      tripItemRequests.push({
        count: stopDuration,
        tripItemType: TripItemType.StopMinutes
      });
    }

    // Add RoundTrip if isRoundTrip is enabled
    if (isRoundTrip) {
      tripItemRequests.push({
        count: 1,
        tripItemType: TripItemType.RoundTrip
      });
    }

    // If no specific items are selected, add a default None item
    if (tripItemRequests.length === 0) {
      tripItemRequests.push({
        count: 0,
        tripItemType: TripItemType.None
      });
    }

    console.log('🔍 Final tripItemRequests:', tripItemRequests);

    

    return {
      type: selectedServiceType || TripType.None,
      durationMinutes,
      endTime: endTimeStr,
      endDate: endDateStr,
      startTime: startTimeStr,
      startDate: startDateISO,
      destination: destination
        ? {
            lat: destination.lat,
            long: destination.lng,
            address: destination.address,
            title: destination.title,
            city: destination.city,
            plaque: destination.plaque,
          }
        : { lat: 0, long: 0, address: '' },
      source: source
        ? {
            lat: source.lat,
            long: source.lng,
            address: source.address,
            title: source.title,
            city: source.city,
            plaque: source.plaque,
          }
        : { lat: 0, long: 0, address: '' },
      tripItemRequests
    };
  }, [
    travelDuration,
    selectedServiceType,
    selectedDate,
    selectedTime,
    destination,
    source,
    trafficPlan,
    suburbanTrip,
    stopDuration,
    isRoundTrip
  ]);





  const handleServiceTypeClick = (type: TripType) => {
    setSelectedServiceType(type);
  };

  const handleInfoClick = (type: TripType) => {
    setSelectedDrawerType(type);
    setDrawerVisible(true);
  };

  const closeDrawer = () => {
    setDrawerVisible(false);
    setSelectedDrawerType(null);
  };

  // Handle location edit
  const handleLocationEdit = (type: "source" | "destination") => {
    const location = type === "source" ? source : destination;
    if (location) {
      setEditingLocationType(type);
      setEditingLocation({ ...location });
      setLocationEditDrawerVisible(true);
    }
  };

  const closeLocationEditDrawer = () => {
    setLocationEditDrawerVisible(false);
    setEditingLocationType(null);
    setEditingLocation(null);
  };

  const saveLocationEdit = () => {
    if (editingLocationType && editingLocation) {
      if (editingLocationType === "source") {
        setSource(editingLocation);
      } else {
        setDestination(editingLocation);
      }
      notification.success({ message: 'جزئیات آدرس با موفقیت ذخیره شد' });
      closeLocationEditDrawer();
    }
  };

  const serviceDescriptions = {
    [TripType.None]: {
      title: 'نامشخص',
      description: 'نوع سفر انتخاب نشده است. لطفاً یکی از گزینه‌های زیر را انتخاب کنید.'
    },
    [TripType.Transfer]: {
      title: 'ترنسفر',
      description: 'از هر فرودگاه٬ هتل و … که بخواهید، شما یا مهمان شما را بین ۲ نقطه از شهر جابجا میکنیم'
    },
    [TripType.Dedicated]: {
      title: 'در اختیار',
      description: 'کرایه خودور به صورت دربست و ساعتی برای انجام امور داخل و حومه شهر'
    },
    [TripType.Intercity]: {
      title: 'بین شهری',
      description: 'خودروی دربست برای سفر بین ۲ شهر'
    }
  };

  return (
    <div className="page-container" style={{ 
      height: 'calc(100vh - 60px)', // Subtract navigation bar height
      display: 'flex', 
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      {/* Trip Type Buttons - Top of Page */}
      <div style={{ 
        backgroundColor: theme.background, 
        padding: '0',
        borderBottom: `1px solid ${theme.border}`,
        width: '100%',
        flexShrink: 0
      }}>
        <div style={{ 
          display: 'flex', 
          width: '100%',
          gap: 8,
          padding: '0'
        }}>
          <div style={{ 
            flex: 1, 
            position: 'relative'
          }}>
            <Button 
              type={selectedServiceType === TripType.Transfer ? "primary" : "default"}
              size='large' 
              block 
              data-type="transfer" 
              style={{ 
                height: 56,
                width: '100%',
                backgroundColor: selectedServiceType === TripType.Transfer ? undefined : theme.background,
                borderColor: selectedServiceType === TripType.Transfer ? undefined : theme.border,
                color: selectedServiceType === TripType.Transfer ? undefined : theme.text.secondary
              }}
              onClick={() => handleServiceTypeClick(TripType.Transfer)}
            >
              ترنسفر
            </Button>
            <Button 
              type="text"
              size='small'
              icon={<InfoCircleOutlined />}
              onClick={() => handleInfoClick(TripType.Transfer)}
              style={{ 
                position: 'absolute', 
                top: 4, 
                left: 4, 
                minWidth: 24, 
                height: 24,
                color: 'lightgray'

              }}
            />
          </div>
          <div style={{ 
            flex: 1, 
            position: 'relative'
          }}>
            <Button 
              type={selectedServiceType === TripType.Dedicated ? "primary" : "default"}
              size='large' 
              block 
              data-type="charter" 
              style={{ 
                height: 56,
                width: '100%',
                backgroundColor: selectedServiceType === TripType.Dedicated ? undefined : theme.background,
                borderColor: selectedServiceType === TripType.Dedicated ? undefined : theme.border,
                color: selectedServiceType === TripType.Dedicated ? undefined : theme.text.secondary
              }}
              onClick={() => handleServiceTypeClick(TripType.Dedicated)}
            >
              در اختیار
            </Button>
            <Button 
              type="text"
              size='small'
              icon={<InfoCircleOutlined />}
              onClick={() => handleInfoClick(TripType.Dedicated)}
              style={{ 
                position: 'absolute', 
                top: 4, 
                left: 4, 
                minWidth: 24, 
                height: 24,
                color: 'lightgray'
              }}
            />
          </div>
          <div style={{ 
            flex: 1, 
            position: 'relative'
          }}>
            <Button 
              type={selectedServiceType === TripType.Intercity ? "primary" : "default"}
              size='large' 
              block 
              data-type="intercity" 
              style={{ 
                height: 56,
                width: '100%',
                backgroundColor: selectedServiceType === TripType.Intercity ? undefined : theme.background,
                borderColor: selectedServiceType === TripType.Intercity ? undefined : theme.border,
                color: selectedServiceType === TripType.Intercity ? undefined : theme.text.secondary
              }}
              onClick={() => handleServiceTypeClick(TripType.Intercity)}
            >
              بین شهری
            </Button>
            <Button 
              type="text"
              size='small'
              icon={<InfoCircleOutlined />}
              onClick={() => handleInfoClick(TripType.Intercity)}
              style={{ 
                position: 'absolute', 
                top: 4, 
                left: 4, 
                minWidth: 24, 
                height: 24,
                color: 'lightgray'
              }}
            />
          </div>
        </div>
      </div>

      {/* Map Section - Takes remaining height */}
      <div style={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column',
        minHeight: 0
      }}>
        <Card className="map-container-wrapper" style={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column',
          margin: 0,
          borderRadius: 0
        }}>
          <div 
            key={mapKey}
            ref={mapRef} 
            style={{ 
              height: '100%', 
              width: '100%',
              borderRadius: '4px',
              flex: 1
            }} 
          />
        </Card>
      </div>

      <div style={{ 
        flexShrink: 0, 
        padding: '0px',
        backgroundColor: theme.background
      }}>
        <Row gutter={0}>

          {/* Location Inputs */}
          {shouldShowField('source') && (
            <Col span={24}>
              <Card>
              <div style={{ position: 'relative' }}>
                {/* Source Input */}
                                 <Row gutter={0} align="middle" style={{ marginBottom: 0 }}>
                  <Col flex="auto">
                    <Input
                      placeholder="مبدا"
                      value={source?.address || ''}
                      readOnly
                      className={`location-input ${activeFieldRef.current === 'source' ? 'active' : ''}`}
                      onClick={() => handleFieldClick("source")}
                      prefix={<EnvironmentOutlined style={{ color: theme.primary }} />}
                      suffix={
                        source ? (
                          <CloseCircleOutlined
                            style={{ color: '#ff4d4f', cursor: 'pointer' }}
                            onClick={e => {
                              e.stopPropagation();
                              clearLocation('source');
                            }}
                          />
                        ) : null
                      }
                      style={{ borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}
                    />
                  </Col>
                  <Col>
                    <Button 
                      icon={<CompassOutlined />} 
                      onClick={() => useCurrentLocation("source")}
                      disabled={!navigator.geolocation}
                      style={{ borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}
                    />
                  </Col>
                  <Col>
                    <Button 
                      icon={<EditOutlined />} 
                      onClick={() => handleLocationEdit("source")}
                      disabled={!source}
                      style={{ borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}
                    />
                  </Col>
                </Row>

                {/* Destination Input */}
                {shouldShowField('destination') && (
                                     <Row gutter={0} align="middle" style={{ marginTop: 0 }}>
                    <Col flex="auto">
                      <Input
                        placeholder="مقصد"
                        value={destination?.address || ''}
                        readOnly
                        className={`location-input ${activeFieldRef.current === 'destination' ? 'active' : ''}`}
                        onClick={() => handleFieldClick("destination")}
                        prefix={<EnvironmentOutlined style={{ color: theme.success }} />}
                        suffix={
                          destination ? (
                            <CloseCircleOutlined
                              style={{ color: '#ff4d4f', cursor: 'pointer' }}
                              onClick={e => {
                                e.stopPropagation();
                                clearLocation('destination');
                              }}
                            />
                          ) : null
                        }
                        style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}
                      />
                    </Col>
                    <Col>
                      <Button 
                        icon={<CompassOutlined />} 
                        onClick={() => useCurrentLocation("destination")}
                        disabled={!navigator.geolocation}
                        style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}
                      />
                    </Col>
                    <Col>
                      <Button 
                        icon={<EditOutlined />} 
                        onClick={() => handleLocationEdit("destination")}
                        disabled={!destination}
                        style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}
                      />
                    </Col>
                  </Row>
                )}

                                 {/* Swap Button - At the beginning of the box
                 {shouldShowField('destination') && (
                   <div style={{
                     position: 'absolute',
                     left: '25%',
                     top: '50%',
                     transform: 'translateY(-50%)',
                     zIndex: 10
                   }}>
                     <Button 
                       type="default"
                       shape="circle"
                       icon={<SwapOutlined style={{ color: theme.text.secondary }} />} 
                       onClick={swapLocations}
                       disabled={!source && !destination}
                       size="small"
                       style={{
                         width: 36,
                         height: 36,
                         display: 'flex',
                         alignItems: 'center',
                         justifyContent: 'center',
                         backgroundColor: '#f5f5f5',
                         border: '1px solid #e8e8e8',
                         boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                         color: '#666'
                       }}
                     />
                   </div>
                 )} */}
              </div>
            </Card>
          </Col>
        )}

        {/* Date and Time Selection */}
        {shouldShowField('dateTimeStart') && (
          <Col span={24}>
            <Card>
              <Row gutter={16}>
                <Col span={12}>
                  <DatePicker
                    placeholder="تاریخ سفر"
                    style={{ width: '100%' }}
                    value={selectedDate}
                    onChange={setSelectedDate}
                    format="YYYY/MM/DD"
                    locale={{
                      lang: {
                        locale: 'fa_IR',
                        placeholder: 'انتخاب تاریخ',
                        rangePlaceholder: ['تاریخ شروع', 'تاریخ پایان'],
                        today: 'امروز',
                        now: 'اکنون',
                        backToToday: 'بازگشت به امروز',
                        ok: 'تایید',
                        clear: 'پاک کردن',
                        month: 'ماه',
                        year: 'سال',
                        timeSelect: 'انتخاب زمان',
                        dateSelect: 'انتخاب تاریخ',
                        weekSelect: 'انتخاب هفته',
                        monthSelect: 'انتخاب ماه',
                        yearSelect: 'انتخاب سال',
                        decadeSelect: 'انتخاب دهه',
                        yearFormat: 'YYYY',
                        dateFormat: 'M/D/YYYY',
                        dayFormat: 'D',
                        dateTimeFormat: 'M/D/YYYY HH:mm:ss',
                        monthBeforeYear: true,
                        previousMonth: 'ماه قبل (PageUp)',
                        nextMonth: 'ماه بعد (PageDown)',
                        previousYear: 'سال قبل (Control + left)',
                        nextYear: 'سال بعد (Control + right)',
                        previousDecade: 'دهه قبل',
                        nextDecade: 'دهه بعد',
                        previousCentury: 'قرن قبل',
                        nextCentury: 'قرن بعد',
                        week: 'هفته',
                        shortWeekDays: ['یک', 'دو', 'سه', 'چهار', 'پنج', 'جمعه', 'شنبه'],
                        shortMonths: ['فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور', 'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند']
                      },
                      timePickerLocale: {
                        placeholder: 'انتخاب زمان',
                      },
                    }}
                  />
                </Col>
                                 <Col span={12}>
                   <DatePicker
                     picker="time"
                     placeholder="ساعت سفر"
                     style={{ width: '100%' }}
                     value={selectedTime}
                     onChange={setSelectedTime}
                     format="HH:mm"
                     showSecond={false}
                     needConfirm={false}
                     minuteStep={15}
                   />
                 </Col>
              </Row>
              {shouldShowField('duration') && (
                <Row gutter={16} style={{ marginTop: 16 }}>
                  <Col span={12}>
                    <Select
                      placeholder="مدت سفر (ساعت)"
                      style={{ width: '100%' }}
                      value={travelDuration}
                      onChange={setTravelDuration}
                      disabled={!(selectedDate && selectedTime && maxDuration >= minDuration)}
                      allowClear
                    >
                      {durationOptions}
                    </Select>
                  </Col>
                  <Col span={12}>
                    {/* Duration field takes full width */}
                  </Col>
                  {selectedDate && selectedTime && maxDuration < minDuration && (
                    <Col span={24} style={{ marginTop: '8px' }}>
                      <span style={{ color: theme.error, fontSize: 12, whiteSpace: 'nowrap' }}>
                        حداقل {minDuration} ساعت باید تا انتهای روز باقی مانده باشد
                      </span>
                    </Col>
                  )}
                </Row>
              )}


            </Card>
          </Col>
        )}
        </Row>
        </div>

        {/* Trip Actions - Bottom of page */}
        <div style={{ 
          backgroundColor: theme.surface,
          borderTop: `1px solid ${theme.border}`,
          width: '100%',
          display: 'flex',
          justifyContent: 'space-around',
          alignItems: 'center',
          padding: '8px 0px',
          flexShrink: 0
        }}>
        {/* Trip Options */}
        <div 
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            cursor: 'pointer',
            padding: '0px',
            borderRadius: '8px',
            transition: 'background-color 0.2s',
            width: '80px'
          }}
          onClick={() => setTripOptionsDrawerVisible(true)}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.background}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          <SettingOutlined style={{ fontSize: 24, marginBottom: 4, color: theme.text.secondary }} />
          <span style={{ fontSize: 12, color: theme.text.secondary }}>گزینه های سفر</span>
        </div>

        {/* Main Request Button */}
        <Button 
          type="primary" 
          size="middle" 
          style={{
            height: 48,
            fontSize: 14,
            fontWeight: 500,
            padding: '0px',
            width: '160px'
          }}
                                           onClick={async () => {
              const tripModel = buildCreateTripModel();
              
              try {
                const data = await httpService.createTrip(tripModel);
                
                // Extract trip ID from response and navigate to trip options
                if (data && data.id) {
                  const tripId = data.id;
                  
                  // Navigate to trip options page with trip ID and details
                  navigate('/home/trip-options', { 
                    state: { 
                      tripId: tripId,
                      tripDetails: tripModel
                    }
                  });
                } else {
                  // If no ID returned, still navigate to trip options with the model
                  navigate('/home/trip-options', { 
                    state: { 
                      tripId: null,
                      tripDetails: tripModel
                    }
                  });
                }
              } catch (error) {
                console.error('❌ CreateTrip API error:', error);
                notification.error({ message: 'خطا در ثبت سفر' });
                
                // Even if API fails, navigate to trip options with the model
                navigate('/home/trip-options', { 
                  state: { 
                    tripId: null,
                    tripDetails: tripModel
                  }
                });
              }
            }}
        >
          درخواست سفر
        </Button>

        {/* Discount Code */}
        <div 
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            cursor: 'pointer',
            padding: '0px',
            borderRadius: '8px',
            transition: 'background-color 0.2s',
            width: '80px'
          }}
          onClick={() => {/* TODO: Add discount code functionality */}}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.background}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          <GiftOutlined style={{ fontSize: 24, marginBottom: 4, color: theme.text.secondary }} />
          <span style={{ fontSize: 12, color: theme.text.secondary }}>کد تخفیف</span>
        </div>
      </div>

      {/* Trip Type Info Drawer */}
      <Drawer
        title={selectedDrawerType && serviceDescriptions[selectedDrawerType as TripType]?.title}
        placement="top"
        onClose={closeDrawer}
        open={drawerVisible}
        height="auto"
        style={{ maxHeight: '60vh', zIndex: 9999 }}
      >
        <Typography.Paragraph style={{ fontSize: 16, lineHeight: 1.8 }}>
          {selectedDrawerType && serviceDescriptions[selectedDrawerType as TripType]?.description}
        </Typography.Paragraph>
        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <Button type="primary" onClick={closeDrawer}>
            بستن
          </Button>
        </div>
      </Drawer>

      {/* Trip Options Drawer */}
      <Drawer
        title="گزینه های سفر"
        placement="bottom"
        onClose={() => setTripOptionsDrawerVisible(false)}
        open={tripOptionsDrawerVisible}
        height="auto"
        style={{ height: 'auto', zIndex: 9999 }}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {/* Stop Duration - Only show for Transfer and Intercity */}
          {(selectedServiceType === TripType.Transfer || selectedServiceType === TripType.Intercity) && (
            <Row align="middle" justify="space-between">
              <Col>
                <Typography.Title level={5} style={{ margin: 0 }}>
                  توقف در سفر
                </Typography.Title>
              </Col>
              <Col>
                <Select
                  placeholder="انتخاب مدت توقف"
                  style={{ width: 150 }}
                  value={stopDuration}
                  onChange={setStopDuration}
                  allowClear
                >
                  <Select.Option value={15}>15 دقیقه</Select.Option>
                  <Select.Option value={30}>30 دقیقه</Select.Option>
                  <Select.Option value={45}>45 دقیقه</Select.Option>
                  <Select.Option value={60}>1 ساعت</Select.Option>
                  <Select.Option value={90}>1.5 ساعت</Select.Option>
                  <Select.Option value={120}>2 ساعت</Select.Option>
                </Select>
              </Col>
            </Row>
          )}

          {/* Round Trip - Only show for Intercity */}
          {selectedServiceType === TripType.Intercity && (
            <Row align="middle" justify="space-between">
              <Col>
                <Typography.Title level={5} style={{ margin: 0 }}>
                  رفت و برگشت
                </Typography.Title>
              </Col>
              <Col>
                <Switch
                  checked={isRoundTrip}
                  onChange={setIsRoundTrip}
                  checkedChildren="فعال"
                  unCheckedChildren="غیرفعال"
                />
              </Col>
            </Row>
          )}

          {/* Traffic Plan - Show for all types */}
          <Row align="middle" justify="space-between">
            <Col>
              <Typography.Title level={5} style={{ margin: 0 }}>
                طرح ترافیک
              </Typography.Title>
            </Col>
            <Col>
              <Switch
                checked={trafficPlan}
                onChange={setTrafficPlan}
                checkedChildren="فعال"
                unCheckedChildren="غیرفعال"
              />
            </Col>
          </Row>

          {/* Suburban Trip - Only show for Dedicated */}
          {selectedServiceType === TripType.Dedicated && (
            <Row align="middle" justify="space-between">
              <Col>
                <Typography.Title level={5} style={{ margin: 0 }}>
                  حومه شهر
                </Typography.Title>
              </Col>
              <Col>
                <Switch
                  checked={suburbanTrip}
                  onChange={setSuburbanTrip}
                  checkedChildren="فعال"
                  unCheckedChildren="غیرفعال"
                />
              </Col>
            </Row>
          )}

          {/* Save Button */}
          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <Button 
              type="primary" 
              onClick={() => setTripOptionsDrawerVisible(false)}
              style={{ width: '100%' }}
            >
              ذخیره گزینه ها
            </Button>
                     </div>
         </Space>
       </Drawer>

       {/* Location Edit Drawer */}
       <Drawer
         title={`ویرایش جزئیات ${editingLocationType === 'source' ? 'مبدا' : 'مقصد'}`}
         placement="bottom"
         onClose={closeLocationEditDrawer}
         open={locationEditDrawerVisible}
         height="auto"
         style={{ height: 'auto', zIndex: 9999 }}
       >
         {editingLocation && (
           <Space direction="vertical" size="middle" style={{ width: '100%' }}>
             {/* Title */}
             <Row align="middle" justify="space-between">
               <Col span={4}>
                 <Typography.Title level={5} style={{ margin: 0 }}>
                   عنوان
                 </Typography.Title>
               </Col>
               <Col span={20}>
                 <Input
                   placeholder="عنوان مکان (اختیاری)"
                   value={editingLocation.title || ''}
                   onChange={(e) => setEditingLocation({ ...editingLocation, title: e.target.value })}
                 />
               </Col>
             </Row>

            {/* Address */}
            <Row align="middle" justify="space-between">
              <Col span={4}>
                <Typography.Title level={5} style={{ margin: 0 }}>
                  آدرس
                </Typography.Title>
              </Col>
              <Col span={20}>
                <Input
                  placeholder="آدرس کامل"
                  value={editingLocation.address}
                  onChange={(e) => setEditingLocation({ ...editingLocation, address: e.target.value })}
                />
              </Col>
            </Row>

            {/* City */}
            <Row align="middle" justify="space-between">
              <Col span={4}>
                <Typography.Title level={5} style={{ margin: 0 }}>
                  شهر
                </Typography.Title>
              </Col>
              <Col span={20}>
                <Input
                  placeholder="نام شهر (اختیاری)"
                  value={editingLocation.city || ''}
                  onChange={(e) => setEditingLocation({ ...editingLocation, city: e.target.value })}
                />
              </Col>
            </Row>

            {/* Plaque */}
            <Row align="middle" justify="space-between">
              <Col span={4}>
                <Typography.Title level={5} style={{ margin: 0 }}>
                  پلاک
                </Typography.Title>
              </Col>
              <Col span={20}>
                <Input
                  placeholder="شماره پلاک (اختیاری)"
                  value={editingLocation.plaque || ''}
                  onChange={(e) => setEditingLocation({ ...editingLocation, plaque: e.target.value })}
                />
              </Col>
            </Row>

            {/* Coordinates Info */}
            <Row align="middle" justify="space-between">
              <Col span={4}>
                <Typography.Title level={5} style={{ margin: 0 }}>
                  
                </Typography.Title>
              </Col>
              <Col span={20}>
                <Typography.Text type="secondary">
                  عرض: {editingLocation.lat.toFixed(6)}, طول: {editingLocation.lng.toFixed(6)}
                </Typography.Text>
              </Col>
            </Row>

             {/* Save Button */}
             <div style={{ textAlign: 'center', marginTop: 16 }}>
               <Button 
                 type="primary" 
                 onClick={saveLocationEdit}
                 style={{ width: '100%' }}
               >
                 ذخیره تغییرات
               </Button>
             </div>
           </Space>
         )}
       </Drawer>
     </div>
   );
 };

export default Home; 